import { Component, EventEmitter, Inject, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { arrayRemove, arrayUpsert, filterNilValue } from '@datorama/akita';
import { debounce, distinctUntilChanged, Subject, take, takeUntil, timer } from 'rxjs';
import { DebouncedValueChange } from '../../global/global.model';
import { GlobalQuery } from '../../global/global.query';
import { Filter } from '../filter.model';
import { FilterOption, FilterType } from '../../../../../../../api/src/data-source/models/filter.model';
import { LocalizationService } from '../../../services/localization.service';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';

@Component({
	selector: 'app-filter-input',
	templateUrl: './filter-input.component.html',
	styleUrls: ['./filter-input.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class FilterInputComponent implements OnChanges {
	protected readonly FilterType = FilterType;

	@Input() filter: Filter;
	@Input() formGroup: UntypedFormGroup;

	@Input() prefixIcon: string;
	@Input() darkMode: boolean = false;
	@Input() debounceMs: number = 750;

	@Output() update: EventEmitter<string> = new EventEmitter();

	public options: FilterOption[];
	public selectedItems: any[] = [];
	public suggestedItems: any[];
	public debouncedValueChange: Subject<DebouncedValueChange> = new Subject();

	public multiSelectSettings: any = {
		labelKey: 'name',
		badgeShowLimit: 0,
		enableSearchFilter: true,
		tagToBody: false
	};

	private unsubscribe$ = new Subject<void>();

	public dateFormat: string = 'MM/DD/YYYY';

	constructor(
		private readonly globalQuery: GlobalQuery,
		private readonly localizationService: LocalizationService,
		private _adapter: DateAdapter<any>,
		@Inject(MAT_DATE_LOCALE) private _locale: string
	) {
		// Debounce changes before they hit the form
		this.debouncedValueChange
			.pipe(
				takeUntil(this.unsubscribe$),
				debounce(change => timer(change.debounceTime)),
				distinctUntilChanged()
			)
			.subscribe(change => {
				this.formGroup.patchValue({
					[change.key]: change.value
				});
				// this.update.emit(change.key);
			});

		this.localizationService.localeCode$.pipe(takeUntil(this.unsubscribe$)).subscribe(code => {
			switch (code) {
				case 'zh-CN':
					this.dateFormat = $localize`:date format@@globalDateFormat:MM/DD/YYYY`;
					this._locale = 'zh-CH';
					this._adapter.setLocale(this._locale);
					break;
				case 'zh-Hant':
					this.dateFormat = $localize`:date format@@globalDateFormat:MM/DD/YYYY`;
					this._locale = 'zh-Hant';
					this._adapter.setLocale(this._locale);
					break;
				default:
					this.dateFormat = $localize`:date format@@globalDateFormat:MM/DD/YYYY`;
					this._locale = 'en-US';
					this._adapter.setLocale(this._locale);
			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		// Load options from the global settings if the filter wants to do that.
		if (this.filter.globalSettingsOptionsKey) {
			this.globalQuery.authenticatedSettings$.pipe(filterNilValue(), take(1)).subscribe(state => {
				this.options = state[this.filter.globalSettingsOptionsKey];
				console.log('global options', this.options, state);
				if (!this.options) {
					console.warn(`Couldn't find the global settings options for this filter`, this.filter);
					this.options = [];
				}
			});
		} else {
			this.options = this.filter.options || [];
		}

		if (this.filter.type === FilterType.MultiSelect) {
			this.selectedItems = this.formGroup.value[this.filter.paramKey] || [];
		}
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	getMultiSelectSettings(text: string) {
		return {
			...this.multiSelectSettings,
			text
		};
	}

	// Call this when you want to debounce a form change
	changeWithDebounce(key: string, value: any, debounceTime = 500) {
		this.debouncedValueChange.next({
			key,
			value,
			debounceTime
		});
	}

	addSelectedItems(items: any[], key: string) {
		items.forEach(item => (this.selectedItems = arrayUpsert(this.selectedItems, item.id, item)));
		this.formGroup.patchValue({
			[key]: this.selectedItems
		});
	}

	removeSelectedItems(items: any[], key: string) {
		console.log('Remove', items, key);
		if (items.length < 1) {
			this.selectedItems = [];
		} else {
			items.forEach(item => (this.selectedItems = arrayRemove(this.selectedItems, item.id)));
		}

		this.formGroup.patchValue({
			[key]: this.selectedItems
		});
	}

	/**
	 * Example of how you could add auto-suggest to an autocomplete input.
	 * @param text
	 */
	getSuggestedItems(text: string) {
		// switch (this.filter.extra?.suggestEntity) {
		// 	case 'user':
		// 		this.userService.suggest(text).pipe(take(1)).subscribe(
		// 			users => (this.suggestedItems = users),
		// 			(err: HttpErrorResponse) => this.globalService.triggerErrorMessage(err)
		// 		);
		// 		break;
		// }
	}

	compareWithId(a: any, b: any) {
		return a?.id === b?.id;
	}
}
